import { format } from 'date-fns';
import { motion, Variants } from 'framer-motion';
import { FC } from 'react';

import {
	Box,
	CalendarOutline,
	DotsHorizontalOutline,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilOutline,
	Portal,
	rem,
	Text,
	TrashOutline,
	useColorModeValue,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';
import { useCurrentUser } from '@bonusly/who-am-i';

import { UserAvatarGroup } from '../../../../components/recognition/bonus/components/user-avatar/user-avatar-group';
import { Goal, GoalStatus } from '../../../../types/goal';
import { useUpdateGoal } from '../../hooks/use-goals';
import { GoalStatusBadge } from './goal-status-badge';

const MotionBox = motion.create(Box);

export interface GoalCardProps {
	goal: Goal;
	onClick?: () => void;
	onEditClick?: () => void;
	onDeleteClick?: () => void;
}

const cardVariants: Variants = {
	hidden: {
		opacity: 0,
		y: 8,
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.4,
			ease: 'easeOut',
		},
	},
};

const badgeVariants: Variants = {
	hidden: {
		scale: 0.8,
		opacity: 0,
	},
	visible: {
		scale: 1,
		opacity: 1,
		transition: {
			delay: 0.1,
			duration: 0.3,
			type: 'spring',
			stiffness: 300,
		},
	},
};

export const GoalCard: FC<GoalCardProps> = ({ goal, onClick, onEditClick, onDeleteClick }) => {
	const cardBg = useColorModeValue('white', 'gray.800');
	const user = useCurrentUser();
	const { data: userCreator } = useUser(goal.userId);
	const updateGoal = useUpdateGoal();
	const toast = useToast();

	// Determine if this is a shared goal (user is not the owner)
	const isSharedGoal = goal.userId !== user?.id;

	const formatRelativeDate = (date: Date) => {
		const now = new Date();
		const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

		if (diffInDays === 0) return 'Today';
		if (diffInDays === 1) return 'Yesterday';
		if (diffInDays < 30) return `${diffInDays} days ago`;
		if (diffInDays < 365) {
			const months = Math.floor(diffInDays / 30);
			return `${months} month${months > 1 ? 's' : ''} ago`;
		}
		const years = Math.floor(diffInDays / 365);
		return `${years} year${years > 1 ? 's' : ''} ago`;
	};

	const hasCollaborators = !!goal.collaborators && goal.collaborators.length > 0;
	const collaborators: any[] = hasCollaborators && userCreator ? [userCreator, ...goal.collaborators] : [];

	const handleStatusChange = async (newStatus: GoalStatus) => {
		if (newStatus === goal.status) return;

		try {
			await updateGoal.mutateAsync({
				id: goal.id,
				data: {
					status: newStatus,
					completedAt: newStatus === 'completed' ? new Date() : undefined,
				},
			});

			toast({
				title: 'Status updated successfully!',
				status: 'success',
				duration: 3000,
			});
		} catch (error) {
			console.error('Failed to update goal status:', error);
			toast({
				title: 'Failed to update status',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	return (
		<Box
			bg={cardBg}
			borderRadius="xl"
			cursor="pointer"
			onClick={onClick}
			position="relative"
			border="1px solid"
			borderColor="border.default"
			boxShadow="none"
			h={rem(274)}
			w="full"
			_hover={{
				boxShadow: 'md',
			}}
			transitionProperty="box-shadow"
			transitionDuration="0.2s"
			transitionTimingFunction="ease-out"
			overflow="hidden"
			data-component="goal-card"
		>
			<MotionBox variants={cardVariants} initial="hidden" animate="visible" display="flex" flexDirection="column" h="full" p={5}>
				{/* Top Row: Status Badge and Kebab Menu */}
				<HStack justify="space-between" w="full" align="start" mb={3}>
					<MotionBox variants={badgeVariants} initial="hidden" animate="visible">
						<GoalStatusBadge status={goal.status} toggleable={!!goal.abilities?.canModify} onStatusChange={handleStatusChange} />
					</MotionBox>

					{(onEditClick || onDeleteClick) && (goal.abilities?.canModify || goal.abilities?.canDelete) && (
						<Menu>
							<MenuButton
								as={IconButton}
								aria-label="Goal options"
								icon={<DotsHorizontalOutline />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ color: 'gray.600', bg: 'gray.100' }}
								onClick={(e) => e.stopPropagation()}
							/>
							<Portal>
								<MenuList>
									{onEditClick && goal.abilities?.canModify && (
										<MenuItem
											icon={<PencilOutline />}
											onClick={(e) => {
												e.stopPropagation();
												onEditClick();
											}}
										>
											Edit goal
										</MenuItem>
									)}
									{onDeleteClick && goal.abilities?.canDelete && (
										<MenuItem
											icon={<TrashOutline />}
											onClick={(e) => {
												e.stopPropagation();
												onDeleteClick();
											}}
										>
											Delete goal
										</MenuItem>
									)}
								</MenuList>
							</Portal>
						</Menu>
					)}
				</HStack>

				{/* Title and Description Block - Flexible content area */}
				<VStack align="start" spacing={2} w="full" flex={1} mb={3}>
					<Text fontWeight="semibold" fontSize="lg" noOfLines={2} color="text.default">
						{goal.title}
					</Text>
					{goal.summary && (
						<Text fontSize="md" color="text.softer" noOfLines={3} fontWeight="normal">
							{goal.summary}
						</Text>
					)}
				</VStack>

				{/* Metadata Row - Fixed to bottom */}
				<HStack justify="space-between" w="full" mt="auto">
					{/* Left: Date Info - Side by side */}
					<HStack spacing={3} align="center">
						{goal.endDate && (
							<HStack spacing={1} align="center">
								<CalendarOutline size="sm" color="text.default" />
								<Text fontSize="sm" fontWeight="medium" color="text.default">
									{format(goal.endDate, 'MMM d')}
								</Text>
							</HStack>
						)}
						<Text fontSize="sm" color="text.softest">
							Updated {formatRelativeDate(new Date(goal.updatedAt))}
						</Text>
					</HStack>

					{/* Right: Avatar Group */}
					<Box mr={2}>
						{hasCollaborators ? (
							<Box position="relative" display="inline-flex">
								<UserAvatarGroup
									max={3}
									size="lg"
									users={collaborators.map((collaborator) => ({
										id: collaborator.id,
										display_name: collaborator.displayName,
										profile_pic_url: collaborator.profilePicUrl || collaborator.profilePic,
										username: collaborator.username,
									}))}
								/>
							</Box>
						) : (
							<Box w="32px" h="32px" /> // Placeholder for consistent spacing
						)}
					</Box>
				</HStack>

				{/* Creator attribution - only shown for shared goals */}
				{isSharedGoal && (
					<Box bgColor="bg.canvas" textAlign="center" p={2} mt={2} mx={-5} mb={-5} borderBottomRadius="xl">
						<Text color="text.softest" size="sm">
							Goal owned by {userCreator?.displayName}
						</Text>
					</Box>
				)}
			</MotionBox>
		</Box>
	);
};
